<template>
  <article class="activities-message"
    :id="activity.id"
    :class="$firefox ? '' : 'scrollmagic__fade-in-up'"
  >
    <header class="activities-header hide--visually">
      <h1>{{ activity.category }}</h1>
      <p class="activities-header__title">{{ headerDate(groupKey) }}</p>
      <time class="activities-header__date">{{ dateFormat(groupKey, 'MMMM DD, YYYY') }}</time>
    </header>

    <div class="activities-legend">
      <div class="activities-legend__item activities-legend__item--icon">
        <i class="integration__icon" :class="'integration__icon--' + slugify(activity.platform)">{{ activity.platform }}</i>
      </div>

      <div class="activities-legend__item activities-legend__item--info">
        <h3 class="activities-legend__name">{{ activity.platform }}</h3>
        <span class="activities-legend__duration"
          :title="dateFormat(activity.created, 'MMMM DD, YYYY hh:mm a')">{{ timeFromNow(activity.created) }}</span>
      </div>

      <div class="activities-legend__item activities-legend__item--menu">
        <ul class="activities-legend-menu" :class="{ 'activities-legend-menu--pinned': activity.saved }">
          <li class="activities-legend-menu__item">
            <button type="button" class="activities-legend-menu__button icon--send" title="Send It">
              <span class="hide--visually">Send It</span>
            </button>
          </li>
          <li class="activities-legend-menu__item activities-legend-menu__item--pin">
            <button type="button" class="activities-legend-menu__button icon--favorite" title="Pin It"
              @click.prevent="pinned(activity.id, activity._links.update.href, activity._links.update.method, true)"
            >
              <span class="hide--visually">Pin It</span>
            </button>
          </li>
          <li class="activities-legend-menu__item activities-legend-menu__item--unpin">
            <button type="button" class="activities-legend-menu__button icon--favorite" title="Unpin It"
              @click.prevent="pinned(activity.id, activity._links.update.href, activity._links.update.method, false)"
            >
              <span class="hide--visually">Unpin It</span>
            </button>
          </li>
          <li class="activities-legend-menu__item">
            <button type="button" class="activities-legend-menu__button icon--more-menu" title="More Options">
              <span class="hide--visually">More Options</span>
            </button>
          </li>
        </ul>
      </div>
    </div>

    <!-- Product -->
    <template v-if="activity.category == 'Product'">
      <div class="activities-product-columns">
        <div class="activities-product-column activities-product-column--left">
          <div class="activities-condition" :class="'activities-condition--' + slugify(activity.level)">
            <p class="activities-condition__level"><i class="activities-condition__bullet"></i>{{ activity.level }}</p>
          </div>

          <h2 class="activities-message__title">{{ activity.title }}</h2>

          <p class="activities-message__info">{{ activity.description }}</p>

          <a class="activities-message__cta" target="_blank"
            :href="`https://${$domain}/admin/products/inventory?query=&inventory_quantity_max=5`"
          >Update Inventory</a>
        </div>

        <div class="activities-product-column activities-product-column--right">
          <ul class="products__list" v-if="activity.data.variants">
            <template v-for="variant in activity.data.variants">
              <keep-alive :key="variant.id">
                <product-tile
                  :key="variant.id"
                  :image="variant.image"
                  :inventory="variant.inventory"
                  :price="variant.price"
                  :title="variant.title"
                  :totalSales="variant.sales.price"
                  :unitsSold="variant.sales.quantity"
                ></product-tile>
              </keep-alive>
            </template>
          </ul>
        </div>
      </div>
    </template>

    <!-- Report -->
    <template v-else-if="activity.category == 'Report'">
      <div class="activities-report-header">
        <div class="activities-report-header__overlay activities-report-header__overlay--image"
          :style="{ backgroundImage: (activity.data.top_products && activity.data.top_products.length > 0 && activity.data.top_products[0].image) ? `url(${ resizedImage(activity.data.top_products[0].image.url, 1000, 1000) })` : 'none' }"
        ></div>
        <div class="activities-report-header__overlay activities-report-header__overlay--gradient"></div>
        <div class="activities-report-header__overlay activities-report-header__overlay--filter"></div>

        <h2 class="activities-report-header__title">{{ activity.title }}</h2>
        <p class="activities-report-header__info">{{ activity.description }}</p>
        <time class="activities-report-header__date">Daily Report for {{ dateFormat(activity.data.date, 'dddd, MMM. DD, YYYY') }}</time>
      </div>

      <div class="activities-report-sales">
        <div class="activities-report-sales-column activities-report-sales-column--left">
          <small class="activities-report-sales__title">Total Sales</small>
          <p class="activities-report-sales__total">{{ activity.data.total_sales | currency }}
            <span class="activities-report-sales__difference" v-if="activity.data.total_sales"
              :class="{ 'activities-report-sales__difference--positive': salesDifference().difference > 0, 'activities-report-sales__difference--negative': salesDifference().difference < 0 }">
              {{ salesDifference().direction }}{{ salesDifference().difference }}%
          </span>
          </p>
        </div>

        <div class="activities-report-sales-column activities-report-sales-column--right">
          <div class="activities-report-sales-item">
            <p class="activities-report-sales__info activities-report-sales__info--total">{{ number(activity.data.total_orders) }}</p>
            <small class="activities-report-sales__info">Orders</small>
          </div>

          <div class="activities-report-sales-item">
            <p class="activities-report-sales__info activities-report-sales__info--total">{{ number(activity.data.units_sold) }}</p>
            <small class="activities-report-sales__info">Products Sold</small>
          </div>
        </div>
      </div>

      <div class="activities-report-dashboard">
        <ul class="dashboard-tile__list" v-if="activity.data.dashboards">
          <template v-for="dashboard in activity.data.dashboards.slice(0, 6)">
            <dashboard-tile
              :key="dashboard.name"
              :amount="number(dashboard.amount)"
              :name="dashboard.name"
              :previous-amount="number(dashboard.previous_amount)"
              :type="dashboard.type"
            ></dashboard-tile>
          </template>
        </ul>
      </div>

      <div class="activities-report-products"
        v-if="activity.data.top_products && activity.data.top_products.length > 0"
      >
        <h3 class="activities-report__subtitle">Top Products</h3>

        <ul class="activities-report-products__list"
          v-for="p in activity.data.top_products"
          :key="p.id"
        >
          <li class="activities-report-products__item activities-report-products__item--left">
            <a class="activities-report-products__link">
              <div class="activities-report-products__culumn">
                <div class="activities-report-products__image"
                  :style="{ backgroundImage: (p.image && p.image !== null) ? `url(${ resizedImage(p.image.url, 100, 100) })` : 'none' }"
                ></div>
              </div>
              <div class="activities-report-products__culumn">
                <div class="activities-report-products__name">{{ p.title }}</div>
              </div>
            </a>
          </li>

          <li class="activities-report-products__item activities-report-products__item--right">
            <div class="activities-report-products__culumn" v-if="p.sales && p.sales.quantity">
              <p class="activities-report-products__total">{{ p.sales.quantity }}</p>
              <small class="activities-report-products__info">Units Sold</small>
            </div>
            <div class="activities-report-products__culumn" v-if="p.sales && p.sales.price">
              <p class="activities-report-products__total">{{ p.sales.price | currency }}</p>
              <small class="activities-report-products__info">Total Sales</small>
            </div>
          </li>
        </ul>
      </div>

      <div class="activities-report-visitors" v-if="gaData">
        <h3 class="activities-report__subtitle">Visitors</h3>

        <ul class="dashboard-tile__list" v-if="visitorTypes">
          <template v-for="(value, key) in visitorTypes">
            <template>
              <dashboard-tile
                :key="key"
                :amount="number(value)"
                :name="capitalizeFirstLetter(key)"
                :previous-amount="number(visitorTypesPrev[key])"
                :type="'int'"
              ></dashboard-tile>
            </template>
          </template>
        </ul>
      </div>

      <div class="activities-report-customers"
        v-if="activity.data.top_customers && activity.data.top_customers.length > 0">
        <h3 class="activities-report__subtitle">Top Customers</h3>

        <table class="activities-table">
          <thead class="activities-table__thead">
            <tr class="activities-table__tr">
              <th class="activities-table__th">Customer</th>
              <th class="activities-table__th">Email</th>
              <th class="activities-table__th">Purchase Total</th>
            </tr>
          </thead>

          <tbody class="activities-table__tbody">
            <tr class="activities-table__tr"
              v-for="c in activity.data.top_customers"
              :key="c.id"
            >
              <td class="activities-table__td"><span>Customer</span>{{c.name }}</td>
              <td class="activities-table__td"><span>Email</span>{{c.email }}</td>
              <td class="activities-table__td"><span>Purchase Total</span>{{c.sales.net_price | currency }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="activities-report-referrers" v-if="referrers">
        <h3 class="activities-report__subtitle">Top Referrers</h3>

        <table class="activities-table">
          <thead class="activities-table__thead">
            <tr class="activities-table__tr">
              <th class="activities-table__th">URL</th>
              <th class="activities-table__th">Visits</th>
            </tr>
          </thead>

          <tbody class="activities-table__tbody">
            <tr class="activities-table__tr"
              v-for="referrer in referrers.slice(0, 5)"
              :key="referrer.label"
            >
              <td class="activities-table__td"><span>URL</span>{{ referrer.label }}</td>
              <td class="activities-table__td"><span>Visits</span>{{ referrer.value }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="activities-report-device" v-if="gaData">
        <h3 class="activities-report__subtitle">Visitors by Device Type</h3>

        <ul class="activities-report-device__list">
          <li class="activities-report-device__item"
            v-for="(value, key) in deviceTypes"
            :key="key"
          >
            <i class="activities-report-device__icon"
              :class="'icon--device-' + key"></i>
            <span class="activities-report-device-info">
              <p class="activities-report-device__result">{{ visitorPercentage(value) }}%</p>
              <small class="activities-report-device__type">{{ capitalizeFirstLetter(key.toLowerCase()) }}</small>
            </span>
          </li>
        </ul>
      </div>

      <div class="activities-report-social" v-if="socialData">
        <h3 class="activities-report__subtitle">Social Followers</h3>

        <ul class="dashboard-tile__list">
          <template v-for="(value, key) in socialData">
            <dashboard-tile
              :key="key"
              :amount="number(value.followers)"
              :name="capitalizeFirstLetter(key)"
              :previous-amount="number(value.previous_followers)"
              :type="'int'"
              :social="key"
            ></dashboard-tile>
          </template>
        </ul>
      </div>
    </template>

    <!-- Promo -->
    <template v-else-if="activity.category == 'Promo'">
      Promo
    </template>

    <!-- General -->
    <template v-else>
      <div class="activities-condition" :class="'activities-condition--' + slugify(activity.level)">
        <p class="activities-condition__level"><i class="activities-condition__bullet"></i>{{ activity.level }}</p>
      </div>

      <h2 class="activities-message__title">{{ activity.title }}</h2>

      <p class="activities-message__info">{{ activity.description }}</p>

      <!-- Import Progress -->
      <template
        v-if="activity.name === 'initial_import'"
      >
        <p><strong>{{ activity.data.products }}</strong> Products</p>
        <div class="activities-progress__container">
          <div class="activities-progress__loader"
            :style="'width:' + (products_import / activity.data.products * 100) + '%'"
          ></div>
        </div>

        <p><strong>{{ activity.data.orders }}</strong> Orders</p>
        <div class="activities-progress__container">
          <div class="activities-progress__loader"
            :style="'width:' + (orders_import / activity.data.orders * 100) + '%'"
          ></div>
        </div>
      </template>

      <!-- Import Complete -->
      <template
        v-if="activity.name === 'initial_import_complete'"
      >
        <router-link class="activities-message__cta" tag="a"
          :to="{ name: 'Products' }"
        >Go to Products</router-link>
      </template>


      <!-- Integration Connect Button -->
      <template
        v-if="integration && showIntegrationConnect()"
      >
        <template v-if="integration.connected">
          <p>You're Connected! 👍</p>
        </template>
        <template v-else>
          <a class="activities-message__cta"
            :href="integration._links.authorize_url.href"
          >Connect</a>
        </template>
      </template>


      <!--<div class="activities-insight">
        <small class="activities-insight__title">Insight</small>

        <p class="activities-insight__info">There's a potential quality control issue with these products? Check to make sure descriptions are accurate, etc.</p>

        <ul class="activities-insight__list">
          <li class="activities-insight__item">
            <i class="activities-insight__icon activities-insight__icon--mailchimp"></i>
          </li>
          <li class="activities-insight__item">
            <p class="activities-insight__referrer">— MailChimp</p>
          </li>
        </ul>
      </div>-->
    </template>
  </article>
</template>

<script>
  import DashboardTile from '@/components/DashboardTile'
  import ProductTile from '@/components/ProductTile'

  import { ga } from '@/mixins/ga.js'
  import { helpers } from '@/mixins/helpers.js'

  import _find from 'lodash/find'
  import axios from 'axios'

  export default {
    name: 'activity',
    components: {
      'dashboard-tile': DashboardTile,
      'product-tile': ProductTile
    },
    mixins: [ga, helpers],
    props: {
      activity: {
        type: Object
      },
      groupKey: {
        type: String
      }
    },
    data () {
      return {
        gaData: null,
        integration: null,
        orders_import: 0,
        products_import: 0,
        socialData: null,
        visitorTypesPrev: null
      }
    },
    methods: {
      applyGoogleData () {
        if (this.activity.data.integrations && this.activity.data.integrations.google_analytics) {
          this.gaData = this.activity.data.integrations.google_analytics
          this.visitors = this.getVisitors(this.gaData.daily_report)
          this.deviceTypes = this.getDeviceTypes(this.gaData.daily_report, 'total_users')
          this.referrers = this.getReferrers(this.gaData.daily_report)
          this.visitorTypes = this.getVisitorTypes(this.gaData.daily_report)
          this.visitorTypesPrev = this.getVisitorTypes(this.gaData.daily_report_previous)
        }
      },
      applySocialData () {
        for (let type of ['facebook', 'twitter', 'instagram']) {
          if (this.activity.data.integrations && this.activity.data.integrations[type]) {
            if (this.socialData === null) {
              this.socialData = {}
            }
            this.socialData[type] = this.activity.data.integrations[type]
          }
        }
      },
      findIntegration () {
        if (!this.$parent.integrations) {
          return false
        }

        let integration = _find(this.$parent.integrations || {}, {'name': this.activity.platform})

        if (integration) {
          this.integration = integration
        }
      },
      importProgress () {
        this.loadUser().then(() => {
          this.products_import = this.$shop.products_import
          this.orders_import = this.$shop.orders_import

          if (this.products_import >= this.activity.data.products && this.orders_import >= this.activity.data.orders) {
            clearInterval(this.interval)
          }
        })
        .catch((error) => {
          console.log(error)
        })
      },
      salesDifference () {
        return this.numberDifference(this.activity.data.total_sales, this.activity.data.previous_total_sales)
      },
      showIntegrationConnect () {
        let connectActivities = ['integration_failed', 'connect_ga', 'connect_social']
        return connectActivities.includes(this.activity.name)
      },
      pinned (id, url, method, val) {
        let that = this

        axios({
          method: method,
          baseURL: url,
          data: {
            saved: val
          }
        })
        .then((response) => {
          that.$emit('pinActivity', id, val)

          let el = document.getElementById(id).querySelector('.activities-legend-menu')
          el.classList.toggle('activities-legend-menu--pinned')
        })
        .catch((error) => {
          console.log(error)
        })
      }
    },
    created () {
      if (this.activity.category === 'Report') {
        this.applyGoogleData()
        this.applySocialData()
      }
      if (this.activity.name === 'initial_import') {
        this.interval = setInterval(() => { this.importProgress() }, 15000)
      }
      this.findIntegration()
    },
    watch: {
      '$parent.integrations': function () {
        this.findIntegration()
      }
    }
  }
</script>


// WEBPACK FOOTER //
// src/js/components/Activity.vue